import { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import {
  FilterFieldConfig,
  FilterOperator,
  FilterRule,
  OPERATORS_BY_TYPE,
  OPERATOR_LABELS,
  UNARY_OPERATORS,
} from '../../view-model/InfluencerList.filterTypes';
import * as styles from './FilterBar.css';

type Step = 'field' | 'operator' | 'value';

type AddFilterButtonProps = {
  fieldConfigs: FilterFieldConfig[];
  onAdd: (rule: FilterRule) => void;
};

export const AddFilterButton = ({
  fieldConfigs,
  onAdd,
}: AddFilterButtonProps) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<Step>('field');
  const [selectedField, setSelectedField] = useState<FilterFieldConfig | null>(
    null,
  );
  const [selectedOperator, setSelectedOperator] =
    useState<FilterOperator | null>(null);
  const [inputValue, setInputValue] = useState('');
  const wrapperRef = useRef<HTMLDivElement>(null);

  const reset = useCallback(() => {
    setOpen(false);
    setStep('field');
    setSelectedField(null);
    setSelectedOperator(null);
    setInputValue('');
  }, []);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        reset();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, reset]);

  const handleFieldSelect = (config: FilterFieldConfig) => {
    setSelectedField(config);
    setStep('operator');
  };

  const handleOperatorSelect = (operator: FilterOperator) => {
    setSelectedOperator(operator);

    if (UNARY_OPERATORS.includes(operator)) {
      onAdd({
        id: crypto.randomUUID(),
        field: selectedField!.key,
        operator,
        value: null,
      });
      reset();
      return;
    }

    setStep('value');
  };

  const handleConfirm = () => {
    if (!selectedField || !selectedOperator) return;

    const trimmedValue = inputValue.trim();

    if (
      (selectedField.fieldType === 'number' ||
        selectedField.fieldType === 'date') &&
      trimmedValue === ''
    ) {
      return;
    }

    let parsedValue: string | number | null = trimmedValue;

    if (selectedField.fieldType === 'number') {
      parsedValue = Number(trimmedValue);
      if (Number.isNaN(parsedValue)) return;
    }

    if (selectedField.fieldType === 'date') {
      if (selectedOperator === 'last-n-days') {
        parsedValue = Number(trimmedValue);
        if (Number.isNaN(parsedValue)) return;
      } else {
        parsedValue = new Date(trimmedValue).getTime();
        if (Number.isNaN(parsedValue)) return;
      }
    }

    if (selectedField.fieldType === 'enum') {
      const enumOption = selectedField.enumOptions?.find(
        (o) => String(o.value) === inputValue,
      );
      parsedValue = enumOption ? enumOption.value : inputValue;
    }

    onAdd({
      id: crypto.randomUUID(),
      field: selectedField.key,
      operator: selectedOperator,
      value: parsedValue,
    });
    reset();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleConfirm();
    if (e.key === 'Escape') reset();
  };

  const renderPopoverContent = () => {
    if (step === 'field') {
      return (
        <>
          <div className={styles.popoverLabel}>Select field</div>
          {fieldConfigs.map((config) => (
            <button
              key={config.key}
              type="button"
              className={styles.popoverOption}
              onClick={() => handleFieldSelect(config)}
            >
              {config.label}
            </button>
          ))}
        </>
      );
    }

    if (step === 'operator' && selectedField) {
      const operators = OPERATORS_BY_TYPE[selectedField.fieldType];
      return (
        <>
          <div className={styles.popoverLabel}>{selectedField.label}</div>
          {operators.map((op) => (
            <button
              key={op}
              type="button"
              className={styles.popoverOption}
              onClick={() => handleOperatorSelect(op)}
            >
              {OPERATOR_LABELS[op]}
            </button>
          ))}
        </>
      );
    }

    if (step === 'value' && selectedField && selectedOperator) {
      return (
        <>
          <div className={styles.popoverLabel}>
            {selectedField.label} {OPERATOR_LABELS[selectedOperator]}
          </div>
          {renderValueInput()}
          <div className={styles.popoverActions}>
            <button
              type="button"
              className={styles.popoverConfirmButton}
              onClick={handleConfirm}
            >
              Apply
            </button>
          </div>
        </>
      );
    }

    return null;
  };

  const renderValueInput = () => {
    if (!selectedField) return null;

    if (selectedField.fieldType === 'enum' && selectedField.enumOptions) {
      return (
        <>
          {selectedField.enumOptions.map((option) => (
            <button
              key={String(option.value)}
              type="button"
              className={styles.popoverOption}
              onClick={() => {
                onAdd({
                  id: crypto.randomUUID(),
                  field: selectedField.key,
                  operator: selectedOperator!,
                  value: option.value,
                });
                reset();
              }}
            >
              {option.label}
            </button>
          ))}
        </>
      );
    }

    if (selectedField.fieldType === 'number') {
      return (
        <input
          type="number"
          className={styles.popoverInput}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Enter number..."
          autoFocus
        />
      );
    }

    if (selectedField.fieldType === 'date') {
      if (selectedOperator === 'last-n-days') {
        return (
          <input
            type="number"
            className={styles.popoverInput}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Number of days..."
            autoFocus
          />
        );
      }

      return (
        <input
          type="date"
          className={styles.popoverInput}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          autoFocus
        />
      );
    }

    return (
      <input
        type="text"
        className={styles.popoverInput}
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Enter value..."
        autoFocus
      />
    );
  };

  return (
    <div className={styles.addFilterWrapper} ref={wrapperRef}>
      <button
        type="button"
        className={styles.addFilterButton}
        onClick={() => setOpen((v) => !v)}
      >
        <FontAwesomeIcon icon={faPlus} size="xs" />
        Add Filter
      </button>

      {open ? (
        <div className={styles.popover}>{renderPopoverContent()}</div>
      ) : null}
    </div>
  );
};
